import { httpClient } from '../http-client';
import { buildQueryString } from './query-builder';
import { ApiResponse } from '../../types/responses/api-response';

// ===== ORGANIZER API TYPES =====

export interface OrganizerMetrics {
  totalEvents: number;
  activeEvents: number;
  totalParticipants: number;
  completedTasks: number;
  totalBudget: number;
  averageBudgetPerEvent: number;
}

export interface OrganizerEvent {
  id: string;
  title: string;
  date: string;
  location: string;
  status: string;
  participants: number;
  completion: number;
}

export interface OrganizerTask {
  id: string;
  title: string;
  description: string;
  status: string;
  priority: string;
  dueDate: string;
  eventId: string;
  eventTitle: string;
}

/**
 * Обзор бюджета организатора — зеркало BE `OrganizerService.getBudgetOverview`.
 *
 * EUR-22: плоской тройки `totalBudget/spentBudget/remainingBudget` тут больше нет.
 * Валюта задаётся СОБЫТИЕМ (`Event.currency`), а у организатора событий может быть
 * несколько и в разных валютах — одно число было бы суммой несопоставимых слагаемых
 * (тот же класс, что EUR-18). Обычный случай «все события в одной валюте» = ровно
 * один элемент в `totals`; печатать список — `formatMixedTotal`.
 */
export interface BudgetOverview {
  totals: Array<{
    currency: string;
    totalBudget: number;
    spentBudget: number;
    remainingBudget: number;
  }>;
  budgetByCategory: Array<{
    category: string;
    currency: string;
    amount: number;
    /** Доля категории ВНУТРИ своей валюты, %. */
    percentage: number;
  }>;
}

export interface ParticipantsInfo {
  totalParticipants: number;
  participantsByEvent: Array<{
    eventId: string;
    eventTitle: string;
    count: number;
  }>;
  newParticipants: number;
}

export interface OrganizerAnalytics {
  eventTrends: Array<{
    month: string;
    events: number;
    participants: number;
  }>;
  topEvents: Array<{
    id: string;
    title: string;
    participants: number;
    rating: number;
  }>;
  participantGrowth: Array<{
    month: string;
    newParticipants: number;
  }>;
}

export interface OrganizerNotification {
  id: string;
  title: string;
  message: string;
  type: string;
  createdAt: string;
  read: boolean;
}

export interface ModerationItem {
  id: string;
  type: string;
  content: string;
  authorId: string;
  authorName: string;
  status: string;
  createdAt: string;
}

export interface QuickStats {
  totalEvents: number;
  activeEvents: number;
  totalParticipants: number;
  completedTasks: number;
}

export interface EventSettings {
  id: string;
  eventId: string;
  allowRegistration: boolean;
  requireApproval: boolean;
  maxParticipants: number;
  notificationsEnabled: boolean;
}

// Query параметры
export interface MetricsQuery {
  period?: 'day' | 'week' | 'month' | 'year';
}

export interface EventsQuery {
  page?: number;
  limit?: number;
  status?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface TasksQuery {
  page?: number;
  limit?: number;
  status?: string;
  priority?: string;
}

export interface NotificationsQuery {
  eventId: string;
  page?: number;
  limit?: number;
  read?: boolean;
}

export interface ModerationQuery {
  eventId: string;
  page?: number;
  limit?: number;
  status?: string;
  type?: string;
}

export interface CreateNotificationData {
  title: string;
  message: string;
  type: 'email' | 'push' | 'sms';
  recipientIds?: string[];
  sendToAll?: boolean;
}

export interface ModerateContentData {
  action: 'approve' | 'reject' | 'flag';
  reason?: string;
}

export interface GenerateReportQuery {
  eventId: string;
  format?: 'pdf' | 'excel' | 'csv';
  includeParticipants?: boolean;
  includeAnalytics?: boolean;
}

export interface UpdateSettingsData {
  allowRegistration?: boolean;
  requireApproval?: boolean;
  maxParticipants?: number;
  notificationsEnabled?: boolean;
}

// ===== ORGANIZER API METHODS =====

export const organizerApi = {
  // ==================== ОСНОВНЫЕ ЭНДПОИНТЫ ====================

  /**
   * Получить основные метрики организатора
   */
  getMetrics: async (query?: MetricsQuery): Promise<ApiResponse<OrganizerMetrics>> => {
    return httpClient.get<OrganizerMetrics>(`/organizer/metrics${buildQueryString(query || {})}`);
  },

  /**
   * Получить последние мероприятия организатора
   */
  getRecentEvents: async (query?: EventsQuery): Promise<ApiResponse<{
    events: OrganizerEvent[];
    total: number;
    page: number;
    limit: number;
  }>> => {
    return httpClient.get(`/organizer/events/recent${buildQueryString(query || {})}`);
  },

  /**
   * Получить задачи, требующие внимания
   */
  getPendingTasks: async (query?: TasksQuery): Promise<ApiResponse<{
    tasks: OrganizerTask[];
    total: number;
    page: number;
    limit: number;
  }>> => {
    return httpClient.get(`/organizer/tasks/pending${buildQueryString(query || {})}`);
  },

  /**
   * Получить обзор бюджета
   */
  getBudgetOverview: async (): Promise<ApiResponse<BudgetOverview>> => {
    return httpClient.get<BudgetOverview>('/organizer/budget/overview');
  },

  /**
   * Получить информацию об участниках
   */
  getParticipants: async (): Promise<ApiResponse<ParticipantsInfo>> => {
    return httpClient.get<ParticipantsInfo>('/organizer/participants');
  },

  /**
   * Получить аналитику мероприятий
   */
  getAnalytics: async (): Promise<ApiResponse<OrganizerAnalytics>> => {
    return httpClient.get<OrganizerAnalytics>('/organizer/analytics');
  },

  // ==================== УВЕДОМЛЕНИЯ ====================

  /**
   * Получить уведомления события
   */
  getNotifications: async (query: NotificationsQuery): Promise<ApiResponse<{
    notifications: OrganizerNotification[];
    total: number;
    page: number;
    limit: number;
  }>> => {
    return httpClient.get(`/organizer/notifications${buildQueryString(query || {})}`);
  },

  /**
   * Создать уведомление для участников
   */
  createNotification: async (eventId: string, data: CreateNotificationData): Promise<ApiResponse<{
    success: boolean;
    message: string;
  }>> => {
    return httpClient.post(`/organizer/notifications${buildQueryString({ eventId })}`, data);
  },

  // ==================== МОДЕРАЦИЯ ====================

  /**
   * Получить контент для модерации
   */
  getModerationItems: async (query: ModerationQuery): Promise<ApiResponse<{
    items: ModerationItem[];
    total: number;
    page: number;
    limit: number;
  }>> => {
    return httpClient.get(`/organizer/moderation${buildQueryString(query || {})}`);
  },

  /**
   * Модерировать контент
   */
  moderateContent: async (eventId: string, contentId: string, data: ModerateContentData): Promise<ApiResponse<{
    success: boolean;
    message: string;
  }>> => {
    return httpClient.put(`/organizer/moderation/${contentId}${buildQueryString({ eventId })}`, data);
  },

  // ==================== ОТЧЕТЫ ====================

  /**
   * Создать отчет по событию
   */
  generateReport: async (query: GenerateReportQuery): Promise<ApiResponse<{
    reportId: string;
    status: string;
    message: string;
  }>> => {
    return httpClient.post(`/organizer/reports${buildQueryString(query || {})}`);
  },

  /**
   * Получить быструю статистику
   */
  getQuickStats: async (eventId: string): Promise<ApiResponse<QuickStats>> => {
    return httpClient.get<QuickStats>(`/organizer/reports/quick-stats${buildQueryString({ eventId })}`);
  },

  // ==================== НАСТРОЙКИ ====================

  /**
   * Получить настройки события
   */
  getEventSettings: async (eventId: string): Promise<ApiResponse<EventSettings>> => {
    return httpClient.get<EventSettings>(`/organizer/settings${buildQueryString({ eventId })}`);
  },

  /**
   * Обновить настройки события
   */
  updateEventSettings: async (eventId: string, data: UpdateSettingsData): Promise<ApiResponse<{
    success: boolean;
    message: string;
  }>> => {
    return httpClient.put(`/organizer/settings${buildQueryString({ eventId })}`, data);
  },

  // ==================== МОБИЛЬНЫЕ ЭНДПОИНТЫ ====================

  /**
   * Получить мобильную панель управления
   */
  getMobileDashboard: async (): Promise<ApiResponse<{
    metrics: OrganizerMetrics;
    upcomingEvents: OrganizerEvent[];
    urgentTasks: OrganizerTask[];
    quickStats: QuickStats;
  }>> => {
    return httpClient.get('/organizer/mobile/dashboard');
  },

  /**
   * Получить быстрый список событий для мобильного
   */
  getQuickEvents: async (limit: number = 5): Promise<ApiResponse<OrganizerEvent[]>> => {
    return httpClient.get<OrganizerEvent[]>(`/organizer/mobile/events/quick${buildQueryString({ limit })}`);
  },

  /**
   * Получить срочные задачи для мобильного
   */
  getUrgentTasks: async (limit: number = 10): Promise<ApiResponse<OrganizerTask[]>> => {
    return httpClient.get<OrganizerTask[]>(`/organizer/mobile/tasks/urgent${buildQueryString({ limit })}`);
  },
};

export default organizerApi;

